/**
 * src/components/ReportProgressReflectionPanel.tsx
 *
 * 工程進捗反映パネル（管理者専用）
 *
 * タブ1: 反映候補 – 未反映の候補一覧、個別/まとめて反映
 * タブ2: 反映履歴 – 反映済みの履歴一覧、安全な取り消し
 */

import { useState, useEffect, useCallback } from 'react'
import {
  api,
  type ReportProgressCandidate,
  type ReportProgressReflectionHistory,
} from '../api/client'
import { Toast } from './ConfirmDialog'

// ──────────────────────────────────────────────────────────
// JST 日付文字列取得（daysAgo 日前）
// ──────────────────────────────────────────────────────────
function jstDate(daysAgo = 0): string {
  const now = new Date()
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000)
  jst.setUTCDate(jst.getUTCDate() - daysAgo)
  return jst.toISOString().slice(0, 10)
}

// ──────────────────────────────────────────────────────────
// UTC 日時文字列 → JST 表示
// DB は "YYYY-MM-DD HH:MM:SS"（SQLite datetime('now')）か
//    "YYYY-MM-DDTHH:MM:SS.sssZ"（ISO 8601）のどちらかで格納
// ──────────────────────────────────────────────────────────
function formatJstDateTime(value?: string | null): string {
  if (!value) return '—'
  // SQLite の "YYYY-MM-DD HH:MM:SS" を ISO に正規化して UTC として解釈
  const normalized = value.includes('T')
    ? value
    : value.replace(' ', 'T') + 'Z'

  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) return String(value)

  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year:     'numeric',
    month:    '2-digit',
    day:      '2-digit',
    hour:     '2-digit',
    minute:   '2-digit',
  }).format(d)
}

function candidateKey(c: ReportProgressCandidate): string {
  return `${c.daily_report_id ?? c.help_work_entry_id}:${c.detail_index}`
}

// 「日報を見る」に渡す一覧表示用ID。通常日報はdaily_report_idそのまま、
// ヘルプ日報は日報一覧マージ（GET /api/reports）と同じ "help:<uuid>" 表記にする。
function toListId(source_type: 'assistant' | 'help' | undefined, dailyReportId: string | null, helpWorkEntryId: string | null | undefined): string {
  if (source_type === 'help' && helpWorkEntryId) return `help:${helpWorkEntryId}`
  return dailyReportId ?? ''
}

// ──────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────
type Props = {
  /** 「日報を見る」クリック時に呼ぶコールバック。daily_report_id を渡す */
  onOpenReport: (dailyReportId: string) => void
}

// ──────────────────────────────────────────────────────────
// コンポーネント
// ──────────────────────────────────────────────────────────
export function ReportProgressReflectionPanel({ onOpenReport }: Props) {

  // ── タブ ─────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'candidates' | 'history'>('candidates')

  // ── 共通: 期間フィルタ ────────────────────────────────────
  const [startDate, setStartDate] = useState(() => jstDate(30))
  const [endDate,   setEndDate]   = useState(() => jstDate(0))

  // ── 反映候補タブ ──────────────────────────────────────────
  const [candidates,   setCandidates]   = useState<ReportProgressCandidate[]>([])
  const [loading,      setLoading]      = useState(false)
  const [onlyPending,  setOnlyPending]  = useState(true)
  const [selected,     setSelected]     = useState<Set<string>>(new Set())
  const [applyingKey,  setApplyingKey]  = useState<string | null>(null)
  const [bulkApplying, setBulkApplying] = useState(false)
  const [bulkConfirm,  setBulkConfirm]  = useState(false)

  // ── 反映履歴タブ ──────────────────────────────────────────
  const [histItems,   setHistItems]   = useState<ReportProgressReflectionHistory[]>([])
  const [histLoading, setHistLoading] = useState(false)
  const [undoTarget,  setUndoTarget]  = useState<ReportProgressReflectionHistory | null>(null)
  const [undoReason,  setUndoReason]  = useState('')
  const [undoing,     setUndoing]     = useState(false)

  // ── Toast ─────────────────────────────────────────────────
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null)

  const showToast = (ok: boolean, msg: string) => {
    setToast({ ok, msg })
    setTimeout(() => setToast(null), 6000)
  }

  // ── 候補取得 ──────────────────────────────────────────────
  const loadCandidates = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = { start_date: startDate, end_date: endDate }
      if (onlyPending) params.only_pending = '1'
      const res = await api.reportProgressReflections.candidates(params)
      setCandidates(res.candidates)
      setSelected(new Set())
    } catch (e) {
      showToast(false, (e as Error).message ?? '読み込みに失敗しました')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, onlyPending]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'candidates') loadCandidates()
  }, [loadCandidates, activeTab])

  // ── 履歴取得 ──────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistLoading(true)
    try {
      const res = await api.reportProgressReflections.history({
        start_date: startDate,
        end_date:   endDate,
      })
      setHistItems(res.history)
    } catch (e) {
      showToast(false, (e as Error).message ?? '履歴の読み込みに失敗しました')
    } finally {
      setHistLoading(false)
    }
  }, [startDate, endDate]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'history') loadHistory()
  }, [loadHistory, activeTab])

  // ── 選択制御 ──────────────────────────────────────────────
  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else               next.add(key)
      return next
    })
  }

  const pendingKeys = candidates
    .filter((c) => !c.reflected && c.pages_delta > 0)
    .map(candidateKey)

  const isAllSelected     = pendingKeys.length > 0 && pendingKeys.every((k) => selected.has(k))
  const isPartialSelected = !isAllSelected && pendingKeys.some((k) => selected.has(k))

  const toggleSelectAll = () => {
    if (isAllSelected) setSelected(new Set())
    else               setSelected(new Set(pendingKeys))
  }

  // ── 単体反映 ──────────────────────────────────────────────
  const handleApply = async (cand: ReportProgressCandidate) => {
    const key = candidateKey(cand)
    setApplyingKey(key)
    try {
      const result = await api.reportProgressReflections.apply({
        source_type:        cand.source_type,
        daily_report_id:    cand.daily_report_id ?? undefined,
        help_work_entry_id: cand.help_work_entry_id ?? undefined,
        help_staff_id:      cand.help_staff_id ?? undefined,
        detail_index:       cand.detail_index,
        episode_id:         cand.episode_id,
        stage_key:          cand.stage_key,
        pages_delta:        cand.pages_delta,
        work_id:            cand.work_id,
        note:               '日報から反映（一覧）',
      })
      if (result.reconciled && result.message) {
        // B-2: progress は既に反映済み、履歴のみ復旧
        showToast(true, result.message)
      } else {
        showToast(
          true,
          `${cand.work_title} #${cand.episode_number} ${cand.stage_label} に +${cand.pages_delta}P 反映しました`,
        )
      }
      loadCandidates()
    } catch (err) {
      showToast(false, (err as Error).message ?? '反映に失敗しました')
    } finally {
      setApplyingKey(null)
    }
  }

  // ── まとめて反映 ──────────────────────────────────────────
  const selectedPending = candidates.filter(
    (c) => selected.has(candidateKey(c)) && !c.reflected && c.pages_delta > 0
  )

  const handleBulkApply = async () => {
    setBulkConfirm(false)
    if (selectedPending.length === 0) return
    setBulkApplying(true)
    try {
      const res = await api.reportProgressReflections.applyBulk({
        items: selectedPending.map((c) => ({
          source_type:        c.source_type,
          daily_report_id:    c.daily_report_id ?? undefined,
          help_work_entry_id: c.help_work_entry_id ?? undefined,
          help_staff_id:      c.help_staff_id ?? undefined,
          detail_index:       c.detail_index,
          episode_id:         c.episode_id,
          stage_key:          c.stage_key,
          pages_delta:        c.pages_delta,
          work_id:            c.work_id,
          note:               '日報から反映（まとめ）',
        })),
      })
      const succeeded  = typeof res.succeeded  === 'number' ? res.succeeded  : selectedPending.length
      const failed     = typeof res.failed     === 'number' ? res.failed     : 0
      const reconciled = typeof res.reconciled === 'number' ? res.reconciled : 0

      if (failed > 0) {
        showToast(false, `${succeeded}件反映しました（${failed}件失敗）`)
      } else if (reconciled > 0) {
        showToast(true, `${succeeded}件反映しました（うち${reconciled}件は履歴復旧）`)
      } else {
        showToast(true, `${succeeded}件を工程進捗へ反映しました`)
      }
    } catch (err) {
      showToast(false, (err as Error).message ?? 'まとめ反映に失敗しました')
    } finally {
      setBulkApplying(false)
      loadCandidates()
    }
  }

  // ── 取り消し ──────────────────────────────────────────────
  const handleUndo = async () => {
    if (!undoTarget) return
    setUndoing(true)
    try {
      await api.reportProgressReflections.undo(undoTarget.id, {
        reason: undoReason || undefined,
      })
      showToast(
        true,
        `${undoTarget.work_title} #${undoTarget.episode_number ?? ''} ${undoTarget.stage_label} の反映を取り消しました`,
      )
      setUndoTarget(null)
      setUndoReason('')
      loadHistory()
    } catch (err) {
      const msg = (err as Error).message ?? '取り消しに失敗しました'
      if (msg.includes('CONFLICT') || msg.includes('別の更新')) {
        showToast(
          false,
          'この工程は反映後に別の更新が入っているため、自動取り消しできません。作品・話数画面で手動調整してください。',
        )
        setUndoTarget(null)
      } else if (msg.includes('ALREADY_UNDONE') || msg.includes('取り消し済み')) {
        showToast(false, 'この反映は既に取り消し済みです')
        setUndoTarget(null)
        loadHistory()
      } else {
        showToast(false, msg)
      }
    } finally {
      setUndoing(false)
    }
  }

  // ── 集計 ──────────────────────────────────────────────────
  const pendingCount = candidates.filter((c) => !c.reflected).length
  const isLoading    = activeTab === 'candidates' ? loading : histLoading

  // ──────────────────────────────────────────────────────────
  // JSX
  // ──────────────────────────────────────────────────────────
  return (
    <div className="card" style={{ marginBottom: 20 }}>

      {/* ── ヘッダー ── */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--paper-3)' }}>

        {/* Row 1: タブ + ローディング + 操作ボタン */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>

          {/* タブボタン */}
          <div style={{
            display: 'flex', borderRadius: 6, overflow: 'hidden',
            border: '1px solid var(--paper-3)', flexShrink: 0,
          }}>
            <button
              onClick={() => setActiveTab('candidates')}
              style={{
                fontSize: 12, padding: '4px 12px', border: 'none', cursor: 'pointer',
                background: activeTab === 'candidates' ? 'var(--blue)' : 'transparent',
                color:      activeTab === 'candidates' ? '#fff' : 'var(--ink-2)',
                fontWeight: activeTab === 'candidates' ? 700 : 400,
              }}
            >
              反映候補
              {!loading && pendingCount > 0 && (
                <span style={{
                  marginLeft: 5,
                  background: activeTab === 'candidates' ? 'rgba(255,255,255,0.3)' : 'var(--amber)',
                  color: '#fff',
                  borderRadius: 8, padding: '0 5px', fontSize: 10, fontWeight: 700,
                }}>
                  {pendingCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              style={{
                fontSize: 12, padding: '4px 12px', border: 'none', cursor: 'pointer',
                borderLeft: '1px solid var(--paper-3)',
                background: activeTab === 'history' ? 'var(--blue)' : 'transparent',
                color:      activeTab === 'history' ? '#fff' : 'var(--ink-2)',
                fontWeight: activeTab === 'history' ? 700 : 400,
              }}
            >
              反映履歴
            </button>
          </div>

          {/* ローディング */}
          {isLoading && (
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>読込中…</span>
          )}

          <div style={{ flex: 1 }} />

          {/* 再読み込み */}
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: 11 }}
            onClick={() => activeTab === 'candidates' ? loadCandidates() : loadHistory()}
            disabled={isLoading}
          >
            再読み込み
          </button>

          {/* まとめて反映（候補タブのみ） */}
          {activeTab === 'candidates' && selectedPending.length > 0 && (
            <button
              className="btn btn-primary btn-sm"
              style={{ fontSize: 11 }}
              disabled={bulkApplying}
              onClick={() => setBulkConfirm(true)}
            >
              {bulkApplying ? '反映中…' : `選択した ${selectedPending.length} 件をまとめて反映`}
            </button>
          )}
        </div>

        {/* Row 2: 説明テキスト */}
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
          {activeTab === 'candidates'
            ? 'アシスタント日報から、作品・話数の工程進捗へ反映できる候補です。内容を確認して反映してください。'
            : '過去の工程進捗への反映履歴です。誤反映した場合は「取り消し」で元に戻せます（反映後に変更がない場合のみ）。'}
        </div>

        {/* Row 3: 期間フィルタ */}
        <div style={{
          display: 'flex', gap: 10, marginTop: 8,
          flexWrap: 'wrap', alignItems: 'center', fontSize: 12,
        }}>
          <span style={{ color: 'var(--ink-3)' }}>期間：</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              fontSize: 12, padding: '3px 6px',
              border: '1px solid var(--paper-3)', borderRadius: 4,
            }}
          />
          <span style={{ color: 'var(--ink-3)' }}>〜</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              fontSize: 12, padding: '3px 6px',
              border: '1px solid var(--paper-3)', borderRadius: 4,
            }}
          />
          {activeTab === 'candidates' && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 4,
              cursor: 'pointer', color: 'var(--ink-2)',
            }}>
              <input
                type="checkbox"
                checked={onlyPending}
                onChange={(e) => setOnlyPending(e.target.checked)}
              />
              未反映のみ表示
            </label>
          )}
        </div>
      </div>

      {/* ────────────────────────────────────────────────────
          反映候補タブ
      ──────────────────────────────────────────────────── */}
      {activeTab === 'candidates' && (
        <>
          {!loading && candidates.length === 0 && (
            <div style={{
              padding: 32, textAlign: 'center',
              fontSize: 13, color: 'var(--ink-3)',
            }}>
              {onlyPending ? '未反映の候補はありません' : '候補がありません（期間を広げてみてください）'}
            </div>
          )}

          {candidates.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: 'center', padding: '6px 8px' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        disabled={pendingKeys.length === 0}
                        title="未反映をすべて選択"
                        ref={(el: HTMLInputElement | null) => {
                          if (el) el.indeterminate = isPartialSelected
                        }}
                      />
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>日付</th>
                    <th style={{ whiteSpace: 'nowrap' }}>担当者</th>
                    <th>作品 / 話数 / 工程</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>日報P</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>現在進捗</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>反映後</th>
                    <th style={{ whiteSpace: 'nowrap' }}>状態</th>
                    <th style={{ whiteSpace: 'nowrap' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((cand) => {
                    const key        = candidateKey(cand)
                    const isApplying = applyingKey === key
                    const isChecked  = selected.has(key)
                    const isComplete = cand.projected_progress_pages >= cand.target_pages

                    return (
                      <tr
                        key={key}
                        style={{
                          background: cand.reflected ? 'var(--paper-2)' : undefined,
                          opacity:    cand.reflected ? 0.6 : 1,
                        }}
                      >
                        {/* チェックボックス */}
                        <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                          {!cand.reflected && cand.pages_delta > 0 && (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSelect(key)}
                            />
                          )}
                        </td>

                        {/* 日付 */}
                        <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {cand.report_date}
                        </td>

                        {/* 担当者 */}
                        <td style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {cand.assistant_name}
                        </td>

                        {/* 作品/話数/工程 */}
                        <td style={{ fontSize: 12 }}>
                          <div style={{ fontWeight: 600 }}>{cand.work_title}</div>
                          <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
                            <span className="mono" style={{ fontSize: 11 }}>#{cand.episode_number}</span>
                            <span className="badge badge-gray" style={{ fontSize: 9, marginLeft: 5 }}>
                              {cand.stage_label}
                            </span>
                          </div>
                        </td>

                        {/* 日報ページ数 */}
                        <td className="mono" style={{
                          textAlign: 'right', fontWeight: 700, fontSize: 13,
                          color: cand.pages_delta > 0 ? 'var(--blue)' : 'var(--ink-3)',
                        }}>
                          {cand.pages_delta > 0 ? `+${cand.pages_delta}P` : '—'}
                        </td>

                        {/* 現在進捗 */}
                        <td className="mono" style={{ textAlign: 'right', fontSize: 12 }}>
                          {cand.current_progress_pages} / {cand.target_pages ?? '—'}P
                        </td>

                        {/* 反映後 */}
                        <td className="mono" style={{
                          textAlign: 'right', fontSize: 12,
                          color: cand.reflected
                            ? 'var(--ink-3)'
                            : isComplete ? 'var(--amber)' : 'var(--blue)',
                          fontWeight: isComplete && !cand.reflected ? 700 : undefined,
                        }}>
                          {cand.projected_progress_pages} / {cand.target_pages ?? '—'}P
                          {isComplete && !cand.reflected && (
                            <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 700 }}>
                              完了候補
                            </div>
                          )}
                        </td>

                        {/* 状態バッジ */}
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {cand.reflected ? (
                            <span className="badge badge-green" style={{ fontSize: 10 }}>✓ 反映済み</span>
                          ) : cand.pages_delta > 0 ? (
                            <span className="badge badge-amber" style={{ fontSize: 10 }}>未反映</span>
                          ) : (
                            <span className="badge badge-gray" style={{ fontSize: 10 }}>対象外</span>
                          )}
                        </td>

                        {/* 操作 */}
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {!cand.reflected && cand.pages_delta > 0 && (
                              <button
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: 10, padding: '2px 8px', height: 24 }}
                                disabled={isApplying || bulkApplying}
                                onClick={() => handleApply(cand)}
                              >
                                {isApplying ? '…' : '反映'}
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ fontSize: 10, padding: '2px 8px', height: 24 }}
                              onClick={() => onOpenReport(toListId(cand.source_type, cand.daily_report_id, cand.help_work_entry_id))}
                              title="日報詳細を開く"
                            >
                              日報
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ────────────────────────────────────────────────────
          反映履歴タブ
      ──────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <>
          {!histLoading && histItems.length === 0 && (
            <div style={{
              padding: 32, textAlign: 'center',
              fontSize: 13, color: 'var(--ink-3)',
            }}>
              この期間の反映履歴はありません
            </div>
          )}

          {histItems.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>日付</th>
                    <th style={{ whiteSpace: 'nowrap' }}>担当者</th>
                    <th>作品 / 話数 / 工程</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>反映P</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>進捗変化</th>
                    <th style={{ whiteSpace: 'nowrap' }}>反映日時（JST）</th>
                    <th style={{ whiteSpace: 'nowrap' }}>状態</th>
                    <th style={{ whiteSpace: 'nowrap' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {histItems.map((item) => (
                    <tr
                      key={item.id}
                      style={{
                        background: item.is_undone ? 'var(--paper-2)' : undefined,
                        opacity:    item.is_undone ? 0.6 : 1,
                      }}
                    >
                      {/* 日付 */}
                      <td className="mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {item.report_date}
                      </td>

                      {/* 担当者 */}
                      <td style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {item.assistant_name || '—'}
                      </td>

                      {/* 作品/話数/工程 */}
                      <td style={{ fontSize: 12 }}>
                        <div style={{ fontWeight: 600 }}>{item.work_title || '—'}</div>
                        <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
                          <span className="mono" style={{ fontSize: 11 }}>
                            #{item.episode_number ?? '—'}
                          </span>
                          <span className="badge badge-gray" style={{ fontSize: 9, marginLeft: 5 }}>
                            {item.stage_label}
                          </span>
                        </div>
                      </td>

                      {/* 反映ページ数 */}
                      <td className="mono" style={{
                        textAlign: 'right', fontWeight: 700, fontSize: 13,
                        color: item.is_undone ? 'var(--ink-3)' : 'var(--blue)',
                        textDecoration: item.is_undone ? 'line-through' : undefined,
                      }}>
                        +{item.pages_delta}P
                      </td>

                      {/* 進捗変化 */}
                      <td className="mono" style={{ textAlign: 'right', fontSize: 12 }}>
                        <span style={{ color: 'var(--ink-3)' }}>{item.progress_before}</span>
                        {' → '}
                        <span style={{
                          color: item.is_undone ? 'var(--ink-3)' : 'var(--blue)',
                          fontWeight: 600,
                        }}>
                          {item.progress_after}
                        </span>
                        {item.target_pages != null && (
                          <span style={{ color: 'var(--ink-3)' }}>/{item.target_pages}P</span>
                        )}
                      </td>

                      {/* 反映日時（JST）*/}
                      <td style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                        <div>{formatJstDateTime(item.applied_at)}</div>
                        {item.applied_by_name && (
                          <div>{item.applied_by_name}</div>
                        )}
                      </td>

                      {/* 状態 */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {item.is_undone ? (
                          <div>
                            <span className="badge badge-gray" style={{ fontSize: 10 }}>取り消し済み</span>
                            {item.undone_at && (
                              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
                                {formatJstDateTime(item.undone_at)}
                              </div>
                            )}
                            {item.undo_reason && (
                              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>
                                {item.undo_reason}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="badge badge-green" style={{ fontSize: 10 }}>✓ 反映済み</span>
                        )}
                      </td>

                      {/* 操作 */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {!item.is_undone && (
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ fontSize: 10, padding: '2px 8px', height: 24, color: 'var(--red)' }}
                              onClick={() => { setUndoTarget(item); setUndoReason('') }}
                            >
                              取り消し
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-ghost"
                            style={{ fontSize: 10, padding: '2px 8px', height: 24 }}
                            onClick={() => onOpenReport(toListId(item.source_type, item.daily_report_id, item.help_work_entry_id))}
                            title="日報詳細を開く"
                          >
                            日報
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── まとめて反映 確認ダイアログ ── */}
      {bulkConfirm && (
        <div className="overlay" onClick={(e) => e.target === e.currentTarget && setBulkConfirm(false)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">まとめて工程進捗へ反映</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setBulkConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {`選択した ${selectedPending.length} 件の候補を工程進捗へ反映します。\n同じ日報明細は二重反映されません。\nよろしいですか？`}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setBulkConfirm(false)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={handleBulkApply}>
                {selectedPending.length} 件を反映する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 取り消し確認ダイアログ ── */}
      {undoTarget && (
        <div
          className="overlay"
          onClick={(e) => e.target === e.currentTarget && !undoing && setUndoTarget(null)}
        >
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div className="modal-title">反映を取り消す</div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setUndoTarget(null)}
                disabled={undoing}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 12 }}>
                以下の反映を取り消します。現在の進捗ページ数が反映後の値と一致している場合のみ実行できます。
              </p>
              <div style={{
                background: 'var(--paper-2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                borderLeft: '3px solid var(--red)',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {undoTarget.work_title} #{undoTarget.episode_number ?? '—'}
                  <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>
                    {undoTarget.stage_label}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                  +{undoTarget.pages_delta}P 反映済み（{undoTarget.progress_before}P → {undoTarget.progress_after}P）
                </div>
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 2 }}>
                  取り消し後: {undoTarget.progress_after}P → {undoTarget.progress_before}P に戻す
                </div>
              </div>
              <label style={{
                display: 'block', fontSize: 12,
                color: 'var(--ink-3)', marginBottom: 4,
              }}>
                取り消し理由（任意）
              </label>
              <input
                type="text"
                value={undoReason}
                onChange={(e) => setUndoReason(e.target.value)}
                placeholder="例: 入力ミスのため"
                disabled={undoing}
                style={{
                  width: '100%', fontSize: 13, padding: '7px 10px',
                  borderRadius: 6, border: '1px solid var(--paper-3)',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setUndoTarget(null)}
                disabled={undoing}
              >
                キャンセル
              </button>
              <button
                className="btn btn-danger"
                onClick={handleUndo}
                disabled={undoing}
              >
                {undoing ? '取り消し中…' : '取り消す'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />
      )}
    </div>
  )
}
